function getP(): any {
  return ( window as any )?.Capacitor?.Plugins?.UtilityPlugin ?? null;
}

function getDefaultScreenCaptureAllowed(): boolean {
  return true;
}

export type AppStartInfo = {
  sessionId?: string;
  version?: string;
  platform?: string;
  deviceId?: string;
  athenaApiUrl?: string;
};

export const UtilityPlugin = {
  async reportAppStart(loadStep: string, status: 'start' | 'success' | 'failed'): Promise<void>
  {
    const p = getP();
    if ( !p || typeof p.reportAppStart !== 'function' )
    {
      return;
    }

    try
    {
      await p.reportAppStart( { loadStep, status } );
    }
    catch ( e )
    {
      console.warn( '[UtilityPlugin] reportAppStart failed:', e );
    }
  },

  async setAnalyticsConsent( allowed: boolean ): Promise<void>
  {
    const p = getP();
    if ( !p || typeof p.setAnalyticsConsent !== 'function' ) return;
    try
    {
      await p.setAnalyticsConsent( { allowed } );
    }
    catch ( e )
    {
      console.warn( '[UtilityPlugin] setAnalyticsConsent failed:', e );
    }
  },

  async getAppStartInfo(): Promise<AppStartInfo | null>
  {
    const p = getP();
    if ( !p || typeof p.getAppStartInfo !== 'function' )
    {
      return null;
    }

    try
    {
      return await p.getAppStartInfo();
    }
    catch ( e )
    {
      console.warn( '[UtilityPlugin] getAppStartInfo failed:', e );
      return null;
    }
  },
  async shareLogs( text: string ): Promise<void>
  {
    const p = getP();
    if ( !p || typeof p.shareLogs !== 'function' )
      return;
    try
    {
      await p.shareLogs( { text } );
    }
    catch ( e )
    {
      console.warn( '[UtilityPlugin] shareLogs failed:', e );
    }
  },

  async shareImage( base64: string, filename: string, text: string = '' ): Promise<void>
  {
    const p = getP();
    if ( !p || typeof p.shareImage !== 'function' )
      return;
    try
    {
      await p.shareImage( { base64, filename, text } );
    }
    catch ( e )
    {
      console.warn( '[UtilityPlugin] shareImage failed:', e );
    }
  },

  async setScreenCaptureAllowed( allowed: boolean ): Promise<boolean>
  {
    const p = getP();
    if ( !p || typeof p.setScreenCaptureAllowed !== 'function' )
    {
      try { localStorage.setItem( 'debug_screen_capture_allowed', allowed ? '1' : '0' ); } catch {}
      return allowed;
    }

    try
    {
      const result = await p.setScreenCaptureAllowed( { allowed } );
      return Boolean( result?.allowed ?? allowed );
    }
    catch ( e )
    {
      console.warn( '[UtilityPlugin] setScreenCaptureAllowed failed:', e );
      return allowed;
    }
  },

  async getScreenCaptureAllowed(): Promise<boolean>
  {
    const p = getP();
    if ( !p || typeof p.getScreenCaptureAllowed !== 'function' )
    {
      try
      {
        const stored = localStorage.getItem( 'debug_screen_capture_allowed' );
        if ( stored !== null ) return stored === '1';
      }
      catch {}
      return getDefaultScreenCaptureAllowed();
    }

    try
    {
      const result = await p.getScreenCaptureAllowed();
      return Boolean( result?.allowed ?? getDefaultScreenCaptureAllowed() );
    }
    catch ( e )
    {
      console.warn( '[UtilityPlugin] getScreenCaptureAllowed failed:', e );
      return getDefaultScreenCaptureAllowed();
    }
  },

  async exitApp(): Promise<void>
  {
    const capacitor = ( window as any )?.Capacitor;

    try
    {
      if ( capacitor?.Plugins?.App?.exitApp )
      {
        await capacitor.Plugins.App.exitApp();
        return;
      }
    }
    catch ( e )
    {
      console.warn( '[UtilityPlugin] App.exitApp failed:', e );
    }

    const p = getP();
    if ( !p )
    {
      try
      {
        window.close();
      }
      catch ( e )
      {
        console.error( '[UtilityPlugin] window.close failed:', e );
      }
      return;
    }

    try
    {
      await p.exitApp();
    }
    catch ( e )
    {
      console.error( '[UtilityPlugin] exitApp failed:', e );
    }
  },
};
